import { Link, useLocation } from 'react-router-dom';
import { classnames } from '@/libs/utils';
import { useAppContext } from '@/context/AppContext/AppContext';
import Icon, { IconName } from '@/components/atoms/Icon';

export type NavItemType = {
  iconName: IconName;
  filledIconName: IconName;
  label: string;
  href: string;
  unseenCount?: number;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  iconContainerClassName?: string;
  showBetaTag?: boolean;
};

export default function NavItem({ navItem }: { navItem: NavItemType }) {
  const location = useLocation();
  const { theme } = useAppContext();

  const isActive = location.pathname.startsWith(navItem.href);

  return (
    <div className={classnames({ '!mb-2': theme === 'goldcast' })}>
      <Link
        to={navItem.href}
        state={{
          previousPathname: location.pathname
        }}
        onClick={navItem.onClick}
      >
        <li className="group relative flex cursor-pointer flex-col items-center space-y-0.5">
          {navItem.unseenCount && !isActive ? (
            <div
              data-testid="nav-item-unseen-count"
              className="absolute right-1 top-0 flex size-4 items-center justify-center rounded-full bg-transcript-highlight-bg text-2xs font-semibold text-transcript-highlight-text"
            >
              {navItem.unseenCount}
            </div>
          ) : null}
          {navItem.showBetaTag && (
            <span
              data-testid="nav-item-beta-tag"
              className="absolute -top-1 left-8 z-10 rounded-md bg-orange-100 px-1 text-center text-[10px] font-semibold leading-[18px] text-deep-orange-600"
            >
              Beta
            </span>
          )}
          <div
            className={classnames(
              'flex size-10 items-center justify-center rounded-lg',
              {
                'bg-gray-300': isActive,
                'group-hover:bg-ghost-hover': !isActive
              },
              navItem.iconContainerClassName ?? ''
            )}
          >
            {isActive ? (
              <Icon name={navItem.filledIconName} className="size-[18px] text-black" />
            ) : (
              <Icon
                name={navItem.iconName}
                className="navigation-icon text-slate-600 group-hover:bg-slate-200 group-hover:text-black"
              />
            )}
          </div>
          <span
            className={classnames('text-c3xs text-xs font-icons', {
              'text-black': isActive,
              'text-slate-600 group-hover:text-black': !isActive
            })}
          >
            {navItem.label}
          </span>
        </li>
      </Link>
    </div>
  );
}
